/* qb-wishlist v0.2.0 — saved items in the Hub drawer.
 *
 * Reads/writes /lists on the worker. Everything is variant-level: Meta's
 * catalogue keys on the bare Shopify variant id, so a product-level save
 * produces ad events that match nothing.
 *
 * Guests see a sign-in prompt rather than a broken list. That is the point of
 * the feature commercially — a wishlist is the one identification incentive
 * that costs no margin, so the sign-in ask IS the product, not a barrier.
 *
 * v0.2.0: share ("psst mum!") — email item(s) to a chosen person via
 * POST /lists/share. Hard rate limits live in the worker; this side's job is
 * the picker (the relationship preset IS the joke) and honest friendly copy
 * when a limit says no — keyed on WHICH rule blocked, because telling someone
 * who hit the yearly cap to "try again next week" is a lie.
 */
(function () {
  "use strict";

  const VERSION = "0.2.1";

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function workerUrl() {
    return (window.QB && window.QB.worker && window.QB.worker.url) || "";
  }

  // Reuses the shared session token (qb-auth.js) rather than minting another.
  async function authToken(customer) {
    if (window.QB && typeof window.QB.token === "function") {
      const shared = await window.QB.token(customer);
      if (!shared) throw new Error("auth_failed");
      window.QB._token = shared;
      return shared;
    }
    if (window.QB && window.QB._token) return window.QB._token;
    const res = await fetch(`${workerUrl()}/auth`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "token", customerId: customer.id, email: customer.email }),
    });
    const data = await res.json();
    if (!res.ok || !data.token) throw new Error(data.error || "auth_failed");
    window.QB._token = data.token;
    return data.token;
  }

  function deviceToken() {
    try {
      return localStorage.getItem("qb_device_token") || undefined;
    } catch (e) {
      return undefined;
    }
  }

  async function fetchList(customer) {
    const token = await authToken(customer);
    // The market this page actually rendered in. Without it the worker prices
    // saved items in SHOP currency, so an AU shopper saw NZD — the same trap
    // the rewards drawer hit. Server-side it is whitelisted to configured
    // markets, so this is a hint, not a passthrough.
    const country = (window.QB && window.QB.shop && window.QB.shop.market) || "";
    const qs = country ? `&country=${encodeURIComponent(country)}` : "";
    const res = await fetch(
      `${workerUrl()}/lists?customerId=${encodeURIComponent(customer.id)}${qs}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "lists_failed");
    return data;
  }

  function money(amount, currency) {
    if (amount == null) return "";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        // The worker returns the currency it priced in; only fall back to the
        // checkout currency when it did not say.
        currency: currency || window.QB?.shop?.checkoutCurrency || "NZD",
      }).format(Number(amount));
    } catch (e) {
      return `${currency || ""} ${Number(amount).toFixed(2)}`.trim();
    }
  }

  // Straight to the Shopify cart. Saved items are already variant-level, so
  // there is nothing left to choose — which is the whole reason a wishlist
  // converts better than a "view product" link.
  async function addToCart(variantId) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!res.ok) throw new Error("cart_failed");
    return res.json();
  }

  function shortDate(iso) {
    if (!iso) return "";
    try {
      return new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(new Date(iso));
    } catch (e) {
      return String(iso).slice(0, 10);
    }
  }

  async function saveFields(customer, productId, variantId, fields) {
    const token = await authToken(customer);
    const res = await fetch(`${workerUrl()}/lists/items`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        customerId: customer.id,
        productId: String(productId),
        variantId: variantId ? String(variantId) : undefined,
        ...fields,
      }),
    });
    if (!res.ok) throw new Error("save_failed");
    return res.json();
  }

  async function mutate(customer, productId, variantId, remove) {
    const token = await authToken(customer);
    const res = await fetch(`${workerUrl()}/lists/items`, {
      method: remove ? "DELETE" : "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        customerId: customer.id,
        productId: String(productId),
        variantId: variantId ? String(variantId) : undefined,
        token: deviceToken(),
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "lists_failed");
    return data;
  }

  // Preset relationships — mirrors the worker's allowlist. The selection
  // drives the email's subject line, so it is part of the joke, not metadata.
  const SHARE_RELS = [
    ["mum", "Mum"],
    ["dad", "Dad"],
    ["nan", "Nan"],
    ["grandad", "Grandad"],
    ["someone", "Someone who loves me"],
  ];

  // Friendly copy per refusal. Raw errors never reach a shopper.
  const SHARE_COPY = {
    account_7d: "You've sent a few of these lately — try again next week.",
    recipient_7d: "They've already had a couple from you this week. Give them a chance to reply!",
    account_365d: "That's the lot for this year — the share button needs a breather.",
    nothing_shareable: "Nothing on your list is in stock to share right now.",
    invalid_recipient: "That email address doesn't look right.",
    default: "Couldn't send it just now — try again in a bit.",
  };

  async function sharePost(customer, payload) {
    const token = await authToken(customer);
    const res = await fetch(`${workerUrl()}/lists/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        customerId: customer.id,
        country: (window.QB && window.QB.shop && window.QB.shop.market) || undefined,
        checkoutCurrency: (window.QB && window.QB.shop && window.QB.shop.checkoutCurrency) || undefined,
        ...payload,
      }),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  }

  function buildShareForm() {
    return `
      <div class="qb-wishlist__sharerow" data-qb-wl-share-form>
        <label class="qb-wishlist__sharelabel">Who's this going to?
          <select data-qb-wl-share-rel>
            ${SHARE_RELS.map(([v, l]) => `<option value="${v}">${l}</option>`).join("")}
          </select>
        </label>
        <input type="email" data-qb-wl-share-email placeholder="their@email.com" autocomplete="email" inputmode="email">
        <button class="qb-wishlist__add" type="button" data-qb-wl-share-send>Send it</button>
        <span class="qb-wishlist__sharemsg" data-qb-wl-share-msg role="status"></span>
      </div>`;
  }

  function buildGuestHtml() {
    const url = (window.QB && window.QB.loginUrl && window.QB.loginUrl("wishlist")) || "/account/login";
    return `
      <div class="qb-wishlist qb-wishlist--guest">
        <p class="qb-wishlist__title">Save what you love</p>
        <p class="qb-wishlist__message">Sign in to keep a list across your devices, and we'll tell you when something you've saved is back or drops in price.</p>
        <a class="qb-wishlist__btn" href="${escapeHtml(url)}">Sign in</a>
      </div>`;
  }

  function buildHtml(state) {
    if (state.loading) return `<div class="qb-wishlist"><p class="qb-wishlist__message">Loading…</p></div>`;
    if (state.available === false) {
      // Feature off (no ledger) — say nothing rather than claim it is empty.
      return `<div class="qb-wishlist"><p class="qb-wishlist__message">Saved items aren't available right now.</p></div>`;
    }
    if (state.error) {
      return `<div class="qb-wishlist"><p class="qb-wishlist__error" role="alert">${escapeHtml(state.error)}</p></div>`;
    }
    if (!state.items.length) {
      return `
        <div class="qb-wishlist">
          <p class="qb-wishlist__title">Nothing saved yet</p>
          <p class="qb-wishlist__message">Tap the heart on anything you like and it'll show up here.</p>
        </div>`;
    }
    return `
      <div class="qb-wishlist">
        <p class="qb-wishlist__title">${state.items.length} saved
          <button class="qb-wishlist__notebtn" type="button" data-qb-wl-share-list>Share list</button>
        </p>
        <ul class="qb-wishlist__items list-unstyled">
          ${state.items
            .map((i) => {
              // Detail comes decorated from the worker. A product that has been
              // deleted or unpublished still lists, just without a link — the
              // customer can still see and clear it.
              const title = i.title || "Saved item";
              const href = i.url
                ? `${i.url}${i.variantId ? `?variant=${encodeURIComponent(i.variantId)}` : ""}`
                : null;
              const sub = [i.variantTitle, i.available === false ? "Sold out" : null]
                .filter(Boolean)
                .join(" · ");
              const price = money(i.price, i.currency);
              const canBuy = i.available !== false && i.variantId;
              return `
              <li class="qb-wishlist__item${i.available === false ? " is-unavailable" : ""}" data-qb-wl-item data-product="${escapeHtml(i.productId)}" data-variant="${escapeHtml(i.variantId || "")}" data-remind-at="${escapeHtml(i.remindAt && !i.remindedAt ? i.remindAt : "")}">
                ${
                  /* Bare <img>, no anchor wrapper. It was wrapped in
                     <a aria-hidden="true"> to avoid a duplicate link to the
                     same product, but plenty of themes ship
                     [aria-hidden="true"]{display:none} and the image vanished.
                     The title beside it already links, so the wrapper bought
                     nothing. */
                  i.image
                    ? `<img class="qb-wishlist__img" src="${escapeHtml(i.image)}" alt="" loading="lazy" width="56" height="56">`
                    : ""
                }
                <span class="qb-wishlist__info">
                  ${href ? `<a class="qb-wishlist__link" href="${escapeHtml(href)}">${escapeHtml(title)}</a>` : `<span class="qb-wishlist__link">${escapeHtml(title)}</span>`}
                  ${sub || price ? `<span class="qb-wishlist__sub">${escapeHtml([sub, price].filter(Boolean).join(" · "))}</span>` : ""}
                  ${i.note ? `<span class="qb-wishlist__note" data-qb-wl-note-text>${escapeHtml(i.note)}</span>` : ""}
                  ${
                    i.remindAt && !i.remindedAt
                      ? `<span class="qb-wishlist__remind">Reminder set for ${escapeHtml(shortDate(i.remindAt))}</span>`
                      : ""
                  }
                  <span class="qb-wishlist__actions">
                    ${canBuy ? `<button class="qb-wishlist__add" type="button" data-qb-wl-add>Add to cart</button>` : ""}
                    <button class="qb-wishlist__notebtn" type="button" data-qb-wl-note>${i.note ? "Edit note" : "Add note"}</button>
                    <button class="qb-wishlist__notebtn" type="button" data-qb-wl-remind>${i.remindAt && !i.remindedAt ? "Change reminder" : "Remind me"}</button>
                    ${i.available !== false ? `<button class="qb-wishlist__notebtn" type="button" data-qb-wl-share>Share</button>` : ""}
                  </span>
                </span>
                <button class="qb-wishlist__remove" type="button" data-qb-wl-remove aria-label="Remove ${escapeHtml(title)}">&times;</button>
              </li>`;
            })
            .join("")}
        </ul>
      </div>`;
  }

  class QBWishlistSlot {
    constructor(panel) {
      this.panel = panel;
      this.state = { loading: true, items: [], available: true, error: null };
      this.bind();
      this.load();
    }

    bind() {
      this.panel.addEventListener("click", async (ev) => {
        const customerNow = window.QB && window.QB.customer;
        const li = ev.target.closest("[data-qb-wl-item]");

        const addBtn = ev.target.closest("[data-qb-wl-add]");
        if (addBtn && li && customerNow) {
          const original = addBtn.textContent;
          addBtn.disabled = true;
          addBtn.textContent = "Adding…";
          try {
            await addToCart(li.dataset.variant);
            addBtn.textContent = "Added";
            // Let the theme's cart drawer/count update itself.
            document.dispatchEvent(new CustomEvent("qb:cart:changed"));
            setTimeout(() => {
              addBtn.textContent = original;
              addBtn.disabled = false;
            }, 1600);
          } catch (e) {
            addBtn.textContent = "Couldn't add";
            setTimeout(() => {
              addBtn.textContent = original;
              addBtn.disabled = false;
            }, 2000);
          }
          return;
        }

        const noteBtn = ev.target.closest("[data-qb-wl-note]");
        if (noteBtn && li && customerNow) {
          const existing = li.querySelector("[data-qb-wl-note-text]");
          const next = window.prompt(
            "Note for this item (e.g. who it's for, or a size to check)",
            existing ? existing.textContent : ""
          );
          if (next === null) return;
          noteBtn.disabled = true;
          try {
            await saveFields(customerNow, li.dataset.product, li.dataset.variant, { note: next.trim() });
            await this.load();
          } catch (e) {
            noteBtn.disabled = false;
          }
          return;
        }

        // Share — one form open at a time, inline in the drawer (no popups,
        // ever — the Hub's whole premise). Per-item shares that item; the
        // header button shares the whole list.
        const shareBtn = ev.target.closest("[data-qb-wl-share], [data-qb-wl-share-list]");
        if (shareBtn && customerNow) {
          const existing = this.panel.querySelector("[data-qb-wl-share-form]");
          const host = shareBtn.closest("[data-qb-wl-item]")
            ? li.querySelector(".qb-wishlist__actions")
            : shareBtn.closest(".qb-wishlist__title");
          if (existing) {
            const wasHere = existing.previousElementSibling === host;
            existing.remove();
            if (wasHere) return; // second tap closes
          }
          host.insertAdjacentHTML("afterend", buildShareForm());
          const form = this.panel.querySelector("[data-qb-wl-share-form]");
          if (shareBtn.hasAttribute("data-qb-wl-share") && li) {
            form.dataset.product = li.dataset.product;
            form.dataset.variant = li.dataset.variant || "";
          }
          form.querySelector("[data-qb-wl-share-email]").focus();
          if (window.QB && window.QB.track) window.QB.track("cta_click", { slot: "wishlist", meta: { label: "share_open" } });
          return;
        }

        const sendBtn = ev.target.closest("[data-qb-wl-share-send]");
        if (sendBtn && customerNow) {
          const form = sendBtn.closest("[data-qb-wl-share-form]");
          const msg = form.querySelector("[data-qb-wl-share-msg]");
          const email = form.querySelector("[data-qb-wl-share-email]").value.trim();
          const relationship = form.querySelector("[data-qb-wl-share-rel]").value;
          if (!email) {
            msg.textContent = SHARE_COPY.invalid_recipient;
            return;
          }
          sendBtn.disabled = true;
          sendBtn.textContent = "Sending…";
          msg.textContent = "";
          try {
            const payload = { relationship, recipientEmail: email };
            if (form.dataset.product) {
              payload.items = [{ productId: form.dataset.product, variantId: form.dataset.variant || "" }];
            }
            const { ok, data } = await sharePost(customerNow, payload);
            if (ok && data.sent) {
              form.innerHTML = `<span class="qb-wishlist__sharemsg qb-wishlist__sharemsg--sent" role="status">Sent! Fingers crossed.</span>`;
              if (window.QB && window.QB.track) window.QB.track("cta_click", { slot: "wishlist", meta: { label: "share_sent" } });
              setTimeout(() => form.remove(), 4000);
            } else {
              // The rule name picks the copy — a yearly block must not
              // promise "next week".
              msg.textContent = SHARE_COPY[data.rule] || SHARE_COPY[data.error] || SHARE_COPY.default;
              sendBtn.disabled = false;
              sendBtn.textContent = "Send it";
            }
          } catch (e) {
            msg.textContent = SHARE_COPY.default;
            sendBtn.disabled = false;
            sendBtn.textContent = "Send it";
          }
          return;
        }

        const remindBtn = ev.target.closest("[data-qb-wl-remind]");
        if (remindBtn && li && customerNow) {
          // <input type="date"> renders in the user's own locale — dd/mm/yyyy
          // in NZ and AU — and always hands back ISO, so there is no format to
          // parse and no dd-mm vs mm-dd ambiguity to get wrong.
          const existing = li.querySelector("[data-qb-wl-date]");
          if (existing) {
            existing.closest(".qb-wishlist__remindrow").remove();
            return;
          }
          const row = document.createElement("div");
          row.className = "qb-wishlist__remindrow";
          const min = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
          row.innerHTML = `
            <input type="date" data-qb-wl-date min="${min}" value="${escapeHtml((li.dataset.remindAt || "").slice(0, 10))}">
            <button type="button" data-qb-wl-date-save>Set</button>
            ${li.dataset.remindAt ? `<button type="button" data-qb-wl-date-clear>Clear</button>` : ""}`;
          li.querySelector(".qb-wishlist__actions").after(row);
          row.querySelector("[data-qb-wl-date]").focus();
          return;
        }

        const saveDate = ev.target.closest("[data-qb-wl-date-save]");
        const clearDate = ev.target.closest("[data-qb-wl-date-clear]");
        if ((saveDate || clearDate) && li && customerNow) {
          const input = li.querySelector("[data-qb-wl-date]");
          const value = clearDate ? "" : (input && input.value) || "";
          if (!clearDate && !value) return;
          (saveDate || clearDate).disabled = true;
          try {
            await saveFields(customerNow, li.dataset.product, li.dataset.variant, { remindAt: value });
            await this.load();
          } catch (e) {
            (saveDate || clearDate).disabled = false;
          }
          return;
        }

        const btn = ev.target.closest("[data-qb-wl-remove]");
        if (!btn) return;
        const customer = customerNow;
        if (!li || !customer || !customer.loggedIn) return;
        btn.disabled = true;
        try {
          await mutate(customer, li.dataset.product, li.dataset.variant, true);
          this.state.items = this.state.items.filter(
            (i) => !(i.productId === li.dataset.product && (i.variantId || "") === li.dataset.variant)
          );
          this.render();
          document.dispatchEvent(new CustomEvent("qb:wishlist:changed"));
        } catch (e) {
          btn.disabled = false;
        }
      });
      // Keep the drawer in step with hearts tapped elsewhere on the page.
      document.addEventListener("qb:wishlist:changed", (ev) => {
        if (ev.detail && ev.detail.from === "slot") return;
        this.load();
      });
    }

    async load() {
      const customer = window.QB && window.QB.customer;
      if (!customer || !customer.loggedIn) {
        this.state.loading = false;
        this.render();
        return;
      }
      try {
        const data = await fetchList(customer);
        this.state.available = data.available !== false;
        this.state.items = data.items || [];
        this.state.error = null;
      } catch (e) {
        this.state.error = "Couldn't load your saved items.";
      }
      this.state.loading = false;
      this.render();
    }

    render() {
      const customer = window.QB && window.QB.customer;
      this.panel.innerHTML =
        customer && customer.loggedIn ? buildHtml(this.state) : buildGuestHtml();
    }
  }

  // ---------------------------------------------------------------------
  // Heart toggles, delegated so PDP and collection cards share one code path
  // and cards rendered later (infinite scroll, quick-shop) work without
  // re-binding.

  const savedKey = (p, v) => `${p}:${v || ""}`;
  let savedSet = null; // null = not loaded yet

  function paintToggles() {
    document.querySelectorAll("[data-qb-wl-toggle]").forEach((btn) => {
      const on = savedSet ? savedSet.has(savedKey(btn.dataset.product, btn.dataset.variant)) : false;
      btn.setAttribute("aria-pressed", on ? "true" : "false");
      btn.classList.toggle("is-saved", on);
      // Swap the label so the control states what it will DO, not just its
      // colour. A filled-vs-outline heart alone is not a readable state.
      const save = btn.querySelector("[data-qb-wl-label-save]");
      const saved = btn.querySelector("[data-qb-wl-label-saved]");
      if (save && saved) {
        save.hidden = on;
        saved.hidden = !on;
      }
    });
  }

  async function loadSaved() {
    const customer = window.QB && window.QB.customer;
    if (!customer || !customer.loggedIn) {
      savedSet = new Set();
      paintToggles();
      return;
    }
    try {
      const data = await fetchList(customer);
      savedSet = new Set((data.items || []).map((i) => savedKey(i.productId, i.variantId)));
    } catch (e) {
      savedSet = new Set();
    }
    paintToggles();
  }

  document.addEventListener("click", async (ev) => {
    const btn = ev.target.closest("[data-qb-wl-toggle]");
    if (!btn) return;
    ev.preventDefault();
    const customer = window.QB && window.QB.customer;
    if (!customer || !customer.loggedIn) {
      // The sign-in ask IS the product here — a wishlist is the one
      // identification incentive that costs no margin.
      const url = (window.QB && window.QB.loginUrl && window.QB.loginUrl("wishlist")) || "/account/login";
      window.location.href = url;
      return;
    }
    const productId = btn.dataset.product;
    // Read the variant live: the shopper may have changed size since paint.
    const form = btn.closest("form[action*='/cart/add']") || document;
    const variantInput = form.querySelector ? form.querySelector("[name='id']") : null;
    const variantId = (variantInput && variantInput.value) || btn.dataset.variant || "";
    const key = savedKey(productId, variantId);
    const wasSaved = savedSet ? savedSet.has(key) : false;

    btn.disabled = true;
    try {
      await mutate(customer, productId, variantId, wasSaved);
      if (!savedSet) savedSet = new Set();
      if (wasSaved) savedSet.delete(key);
      else savedSet.add(key);
      btn.dataset.variant = variantId;
      paintToggles();
      document.dispatchEvent(new CustomEvent("qb:wishlist:changed"));
    } catch (e) {
      /* leave the button as it was; a failed save must not look like a save */
    } finally {
      btn.disabled = false;
    }
  });

  // Repaint when the shopper switches variant — saved state is per size.
  document.addEventListener("change", (ev) => {
    if (ev.target && ev.target.name === "id") {
      document.querySelectorAll("[data-qb-wl-toggle]").forEach((b) => {
        b.dataset.variant = ev.target.value;
      });
      paintToggles();
    }
  });

  document.addEventListener("qb:wishlist:changed", () => {
    if (savedSet) paintToggles();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", loadSaved);
  } else {
    loadSaved();
  }

  window.QB = window.QB || {};
  window.QB.slotModules = window.QB.slotModules || {};
  window.QB.slotModules.wishlist = {
    version: VERSION,
    init(panel) {
      return new QBWishlistSlot(panel);
    },
  };
})();
